import { Injectable, inject } from '@angular/core';
import { Observable, of, map, catchError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { GTMConfig } from './google-tag-manager.service';
import { SvcHttpClient } from 'projects/lib-shared-common/src/public-api';

@Injectable({
  providedIn: 'root',
})
export class GtmConfigService {
  private readonly GTM_CONTAINER_ID_PARAM_NAME = 'GTM_CONTAINER_ID';
  private httpClient = inject(HttpClient);
  private appConfig = inject(AppEnvironmentConfig);
  private svcHttpClient = new SvcHttpClient(this.appConfig.APIs.apiUrlAdministration, this.httpClient);

  getGtmConfig(): Observable<GTMConfig | null> {
    return this.svcHttpClient.get<string | { key: string; value: string }>(`/Parameter/global/value/cached/${this.GTM_CONTAINER_ID_PARAM_NAME}`).pipe(
      map((response) => {
        let gtmId: string | null = null;

        // Handle different response formats
        if (typeof response === 'string') {
          // Direct string response
          gtmId = response.trim();
        } else if (response && typeof response === 'object') {
          // Object response with value property
          if (response.value && typeof response.value === 'string') {
            gtmId = response.value.trim();
          } else if (response.key && typeof response.key === 'string') {
            // Key property
            gtmId = response.key.trim();
          }
        }

        if (gtmId && gtmId.length > 0) {
          const gtmConfig: GTMConfig = {
            id: gtmId,
            environment: this.getEnvironmentFromHost(),
            debug: this.isDebugMode(),
          };
          return gtmConfig;
        } else {
          console.warn('GTM Config validation failed:', {
            response,
            responseType: typeof response,
            extractedGtmId: gtmId,
          });
          return null;
        }
      }),
      catchError((error) => {
        console.warn('Error getting GTM configuration via site parameters:', error);
        return of(null);
      })
    );
  }

  private getEnvironmentFromHost(): string {
    const host = location.host;

    if (host.startsWith('localhost')) {
      return 'local';
    } else if (host.startsWith('qa.')) {
      return 'qa';
    } else if (host.startsWith('devsp.')) {
      return 'development';
    } else if (host.startsWith('sandboxefeso.') || host.startsWith('sandboxrevamp.') || host.startsWith('sandboxrt.')) {
      return 'rc';
    } else if (host.startsWith('demo.')) {
      return 'demo';
    } else if (host.startsWith('www.')) {
      return 'generic';
    } else {
      return 'production';
    }
  }

  private isDebugMode(): boolean {
    const host = location.host;
    return (
      host.startsWith('localhost') ||
      host.startsWith('qa.') ||
      host.startsWith('devsp.') ||
      host.startsWith('sandboxefeso.') ||
      host.startsWith('sandboxrevamp.') ||
      host.startsWith('sandboxrt.') ||
      host.startsWith('demo.')
    );
  }
}
